import {Icon} from '@udemy-v2/icons';
import {Button} from '@udemy-v2/react-core-components';
import {StarRating, StarRatingProps} from '@udemy-v2/react-merchandising-components';
import classNames from 'classnames';
import React from 'react';

import styles from './review-summary-widget.module.scss';
import {useI18n} from '@udemy-v2/i18n';

interface ReviewSummaryRatingGaugeProps {
    rating: number;
    percent: number;
    size: StarRatingProps['size'];
    selectedRating?: number;
    onRatingSelected?: (rating: number | undefined) => void;
    ariaLabel: string;
}

export const ReviewSummaryRatingGauge = ({
    rating,
    percent,
    size,
    selectedRating,
    onRatingSelected,
    ariaLabel,
}: ReviewSummaryRatingGaugeProps) => {
    const {gettext, interpolate} = useI18n();

    const handleRatingSelected = () => {
        if (percent > 0) {
            const newRating = selectedRating === rating ? undefined : rating;
            onRatingSelected?.(newRating);
        }
    };

    const roundedPercent = Math.round(percent);
    let percentLabel;
    if (percent > 0 && roundedPercent === 0) {
        percentLabel = gettext('< 1%');
    } else {
        percentLabel = interpolate(gettext('%(percent)s%'), {percent: roundedPercent}, true);
    }

    const isRatingSelected = selectedRating === rating;
    const isDisabled = percent <= 0;
    const isInactive = !!selectedRating && !isRatingSelected;

    const gaugeStyle = classNames(styles['rates-row'], {
        [styles.disabled]: isDisabled,
        [styles.inactive]: isInactive,
    });

    return (
        <Button
            udStyle="link"
            size="small"
            typography="ud-heading-xs"
            data-purpose={`rate-gauge-${rating}`}
            disabled={isDisabled}
            onClick={handleRatingSelected}
            className={gaugeStyle}
            aria-pressed={isRatingSelected}
        >
            <span className={styles['rate-gauge']}>
                <span className={styles['rate-gauge--bg']}>
                    <span
                        className={styles['rate-gauge--fill']}
                        style={{width: `${roundedPercent}%`}}
                    />
                </span>
            </span>
            <StarRating aria-label={ariaLabel} rating={rating} size={size} />
            <span className={classNames(styles['rate-percent'], 'ud-text-sm')}>
                <span data-purpose="percent-label">{percentLabel}</span>
            </span>
            <span className={styles['rate-clear']}>
                {isRatingSelected ? <Icon glyph="close" label={false} size="xsmall" /> : null}
            </span>
        </Button>
    );
};
